import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { MapPin, X, Locate, Search, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const pickupIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const deliveryIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function ClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&countrycodes=in`);
    const data = await res.json();
    // Prefer Nominatim's `display_name` — it's the rich, human-readable
    // string the autocomplete also uses. The previous behaviour built a
    // string from sparse address-component fields, which collapsed to
    // just the 6-digit pincode whenever road/neighbourhood/suburb/city
    // weren't returned by OSM (common for new layouts, isolated points).
    const display = (data?.display_name || "").trim();
    if (display) return display;
    if (data?.address) {
      const a = data.address;
      const parts = [a.road, a.neighbourhood, a.suburb, a.city || a.town || a.village, a.state_district, a.state, a.postcode].filter(Boolean);
      const composed = parts.join(", ").trim();
      // Reject results that are just a 6-digit pincode — that's the
      // exact bug we are fixing here. Force the caller to refine the
      // map pin instead of persisting a pincode-only address.
      if (composed && !/^\d{6}$/.test(composed)) return composed;
    }
    return "";
  } catch (err) {
    console.warn("reverseGeocode failed:", err);
    return "";
  }
}

// Forward search via Nominatim (free, no key, India-only).
async function searchPlaces(query) {
  if (!query || query.trim().length < 3) return [];
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=in&limit=6`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn("searchPlaces failed:", err);
    return [];
  }
}

export default function MapPicker({ mode = "pickup", onSelect, onClose, initialCenter }) {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const mapRef = useRef(null);
  const searchTimerRef = useRef(null);
  const center = initialCenter || [20.5937, 78.9629]; // India center

  const handleMapClick = async (latlng) => {
    const { lat, lng } = latlng;
    // India bounds check
    if (lat < 6.5 || lat > 35.5 || lng < 68 || lng > 97.5) return;
    setPosition([lat, lng]);
    setLoading(true);
    const addr = await reverseGeocode(lat, lng);
    setAddress(addr);
    setLoading(false);
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          if (lat < 6.5 || lat > 35.5 || lng < 68 || lng > 97.5) return;
          setPosition([lat, lng]);
          if (mapRef.current) mapRef.current.flyTo([lat, lng], 15);
          setLoading(true);
          const addr = await reverseGeocode(lat, lng);
          setAddress(addr);
          setLoading(false);
        },
        () => {}
      );
    }
  };

  // Debounced search-as-you-type — fires 350ms after the last keystroke.
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (v.trim().length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      const results = await searchPlaces(v);
      setSearchResults(results);
      setSearching(false);
    }, 350);
  };

  const pickSearchResult = async (place) => {
    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);
    setPosition([lat, lng]);
    if (mapRef.current) mapRef.current.flyTo([lat, lng], 14);
    setSearchQuery(place.display_name);
    setSearchResults([]);
    setLoading(true);
    const addr = await reverseGeocode(lat, lng);
    setAddress(addr || place.display_name);
    setLoading(false);
  };

  const handleConfirm = () => {
    if (position && address) {
      onSelect({ lat: position[0], lng: position[1], address });
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4"
      data-testid="map-picker-overlay"
      // CRITICAL: when MapPicker opens on top of a Radix Dialog (booking
      // modal), Radix sets `pointer-events: none` on <body> for focus
      // trapping. Our portal target is `document.body`, so EVERYTHING
      // inside this overlay — including the Leaflet map's tap handler —
      // would inherit `none` and silently swallow taps. The explicit
      // `pointerEvents: 'auto'` here re-enables event handling for the
      // portal subtree. Same root cause as the Sprint 19 Razorpay UPI
      // tile issue.
      style={{ pointerEvents: "auto" }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-scaleIn"
        style={{ pointerEvents: "auto" }}
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="map-picker-dialog"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <MapPin className={`h-5 w-5 ${mode === "pickup" ? "text-[#FF6B00]" : "text-green-600"}`} />
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">
              {mode === "pickup" ? "Select Pickup Location" : "Select Delivery Location"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="map-picker-close">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {/* ─── Search bar ──────────────────────────────────────── */}
        <div className="px-5 pt-3 pb-2 relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search for an area, locality or landmark…"
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-100 transition-all outline-none"
              data-testid="map-search-input"
              autoComplete="off"
            />
            {searching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
            )}
          </div>
          {searchResults.length > 0 && (
            <div className="absolute left-5 right-5 mt-1 bg-white rounded-xl border border-slate-200 shadow-lg z-[1100] max-h-56 overflow-y-auto" data-testid="map-search-results">
              {searchResults.map((place) => (
                <button
                  key={place.place_id}
                  onClick={() => pickSearchResult(place)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                  data-testid={`map-search-result-${place.place_id}`}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-slate-700 leading-snug line-clamp-2">{place.display_name}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative h-[350px]">
          <MapContainer center={center} zoom={5} style={{ height: "100%", width: "100%" }} ref={mapRef} zoomControl={false}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
            <ClickHandler onMapClick={handleMapClick} />
            {position && <Marker position={position} icon={mode === "pickup" ? pickupIcon : deliveryIcon} />}
          </MapContainer>
          <button
            onClick={handleLocateMe}
            className="absolute top-3 right-3 z-[1000] bg-white rounded-xl p-2.5 shadow-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            data-testid="map-locate-me"
          >
            <Locate className="h-5 w-5 text-[#FF6B00]" />
          </button>
          <div className="absolute bottom-3 left-3 right-3 z-[1000]">
            <p className="text-xs text-white bg-black/60 rounded-lg px-3 py-1.5 backdrop-blur-sm">
              Search above or tap on the map to {mode === "pickup" ? "select pickup" : "select delivery"} location
            </p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 space-y-3">
          {loading ? (
            <p className="text-sm text-slate-400 animate-pulse">Fetching address...</p>
          ) : address ? (
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">{mode === "pickup" ? "Pickup" : "Delivery"} Address</p>
              <p className="text-sm text-slate-800 font-medium" data-testid="map-picker-address">{address}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No location selected yet</p>
          )}
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1 rounded-xl h-11 border-slate-200">Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={!position || !address}
              className="flex-1 rounded-xl h-11 bg-[#FF6B00] text-white hover:bg-[#E65A00] font-semibold btn-press"
              data-testid="map-picker-confirm"
            >
              <MapPin className="h-4 w-4 mr-2" />
              Confirm Location
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
